import * as React from 'react'
import * as ToastPrimitives from '@radix-ui/react-toast'
import { cva, type VariantProps } from 'class-variance-authority'

import { faXmark } from '@document-crunch/pro-regular-svg-icons'

import { cn } from '@dc-shared-web/lib'
import { Icon } from '@dc-shared-web/components/base/Icon'
import {
  ToastClose as VssToastClose,
  ToastAction as VssToastAction,
  ToastTitle as VssToastTitle,
  ToastDescription as VssToastDescription,
} from '@dc-shared-web/components/base/VssToast'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> & {
    position?: 'top' | 'bottom'
  }
>(({ className, position = 'top', ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'tw-fixed tw-left-[50%] tw-z-[100] tw-flex tw-max-h-screen tw-w-full tw-translate-x-[-50%] tw-flex-col-reverse p-4 sm:tw-right-0 sm:tw-flex-col md:tw-max-w-[420px] tw-break-words',
      position === 'bottom' ? 'tw-bottom-4' : 'tw-top-0',
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

type ToastVariantType = 'default' | 'error' | 'success'

const originalToastVariants = cva(
  'tw-group tw-pointer-events-auto tw-relative tw-flex tw-w-full tw-items-center tw-justify-between tw-space-x-4 tw-overflow-hidden tw-rounded-md tw-border tw-p-6 tw-pr-8 tw-shadow-lg tw-transition-all data-[swipe=cancel]:tw-translate-x-0 data-[swipe=end]:tw-translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:tw-translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:tw-transition-none data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[swipe=end]:tw-animate-out data-[state=closed]:tw-fade-out-80 data-[state=closed]:tw-slide-out-to-top-full data-[state=open]:tw-slide-in-from-top-full',
  {
    variants: {
      variant: {
        default: 'tw-border tw-bg-foreground tw-text-background',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const newToastVariants = cva(
  'tw-group tw-pointer-events-auto tw-relative tw-flex tw-w-full tw-items-center tw-justify-between tw-space-x-4 tw-overflow-hidden tw-rounded-md tw-border tw-p-4 tw-shadow-lg tw-transition-all data-[swipe=cancel]:tw-translate-x-0 data-[swipe=end]:tw-translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:tw-translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:tw-transition-none data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[swipe=end]:tw-animate-out data-[state=closed]:tw-fade-out-80 data-[state=open]:tw-slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'tw-border tw-bg-background tw-text-foreground',
        error:
          'tw-destructive tw-group tw-border-destructive tw-bg-destructive-600 tw-text-destructive-foreground',
        success:
          'tw-success tw-group tw-border-success-border-muted tw-bg-success-muted tw-text-success-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export const toastVariants = (
  props: { variant?: ToastVariantType } & Omit<
    VariantProps<typeof originalToastVariants>,
    'variant'
  >
) => {
  if (typeof window !== 'undefined' && window.__VSS_UPDATES__?.toastEnabled) {
    return newToastVariants(props)
  }
  // Cast to original variant type when using originalToastVariants
  return originalToastVariants({ ...props, variant: 'default' })
}

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Root
    ref={ref}
    className={cn(toastVariants({ variant }), className)}
    {...props}
  />
))
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => {
  if (typeof window !== 'undefined' && window.__VSS_UPDATES__?.toastEnabled) {
    return <VssToastAction ref={ref} {...props} />
  }

  return (
    <ToastPrimitives.Action
      ref={ref}
      className={cn(
        'tw-inline-flex tw-h-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border tw-bg-transparent tw-px-3 tw-text-sm tw-font-medium tw-ring-offset-background tw-transition-colors hover:tw-bg-secondary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50 group-[.destructive]:tw-border-muted/40 group-[.destructive]:hover:tw-border-destructive/30 group-[.destructive]:hover:tw-bg-destructive group-[.destructive]:hover:tw-text-destructive-foreground group-[.destructive]:focus:tw-ring-destructive',
        className
      )}
      {...props}
    />
  )
})
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => {
  if (typeof window !== 'undefined' && window.__VSS_UPDATES__?.toastEnabled) {
    return <VssToastClose ref={ref} {...props} />
  }

  return (
    <ToastPrimitives.Close
      ref={ref}
      className={cn(
        'tw-absolute tw-right-2 tw-top-2 tw-rounded-md tw-p-1 tw-text-foreground/50 tw-opacity-0 tw-transition-opacity hover:tw-text-foreground focus:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 group-hover:tw-opacity-100 group-[.destructive]:tw-text-red-300 group-[.destructive]:hover:tw-text-red-50 group-[.destructive]:focus:tw-ring-red-400 group-[.destructive]:focus:tw-ring-offset-red-600',
        className
      )}
      toast-close=''
      {...props}
    >
      <Icon icon={faXmark} />
    </ToastPrimitives.Close>
  )
})
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => {
  if (typeof window !== 'undefined' && window.__VSS_UPDATES__?.toastEnabled) {
    return <VssToastTitle ref={ref} {...props} />
  }

  return (
    <ToastPrimitives.Title
      ref={ref}
      className={cn('tw-text-sm tw-font-semibold', className)}
      {...props}
    />
  )
})
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => {
  if (typeof window !== 'undefined' && window.__VSS_UPDATES__?.toastEnabled) {
    return <VssToastDescription ref={ref} {...props} />
  }

  return (
    <ToastPrimitives.Description
      ref={ref}
      className={cn('tw-text-sm tw-opacity-90', className)}
      {...props}
    />
  )
})
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
